import { useEffect, useState } from "react";
import { TrendingUp, Users, ShoppingBag, Percent, Building2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { formatRupiah, formatNumber } from "@/lib/dummyData";
import { AdminAPI } from "@/lib/api";

// Dashboard read-only untuk Pengawas (gabungan eks-Investor): GMV, pertumbuhan, ROI, & bagi hasil per wilayah.
const WILAYAH_LABEL = {
  KOPERASI_PUSAT: "SHU Pusat", KANTOR_WILAYAH: "Kantor Wilayah", KANTOR_DAERAH: "Kantor Daerah",
  KANTOR_CABANG: "Kantor Cabang", KANTOR_RANTING: "Kantor Ranting", UNIT_GERAI: "Unit Gerai",
};

export default function InvestorReport() {
  const [data, setData] = useState(null);
  useEffect(() => { AdminAPI.investorReport().then(setData).catch(() => {}); }, []);

  const monthly = (data?.monthly || []).map((m) => ({ month: m.ym, gmv: Math.round(m.gmv / 1000) }));
  let cum = 0;
  const growth = (data?.member_growth || []).map((m) => { cum += m.baru; return { month: m.ym, anggota: cum }; });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-amber-warm font-semibold">Akses Pengawas · Read-only</div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight mt-1">Laporan Investor</h1>
        <p className="text-ink/75 mt-1.5 text-sm">Gross Merchandise Value, pertumbuhan, dan performa ROI wilayah.</p>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={TrendingUp} label="GMV Total" value={formatRupiah(data?.gmv || 0)} />
        <Kpi icon={ShoppingBag} label="Total Transaksi" value={formatNumber(data?.total_orders || 0)} />
        <Kpi icon={Users} label="Total Anggota" value={formatNumber(data?.total_members || 0)} />
        <Kpi icon={Percent} label="Rasio Margin (ROI)" value={`${data?.roi_ratio ?? 0}%`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Tren GMV Bulanan</h3>
          <p className="text-xs text-ink/55">dalam ribuan rupiah</p>
          <div className="h-64 mt-4">
            <ResponsiveContainer>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <Tooltip />
                <Bar dataKey="gmv" radius={[8, 8, 0, 0]}>
                  {monthly.map((_, i) => <Cell key={i} fill={i === monthly.length - 1 ? "#D48C45" : "#2D5A27"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="rounded-xl border-stone-soft p-6">
          <div className="flex items-center gap-2 mb-1"><Building2 size={16} className="text-forest" /><h3 className="font-display font-semibold text-ink">Bagi Hasil per Wilayah</h3></div>
          <p className="text-xs text-ink/55">akumulasi dari transaksi selesai</p>
          <div className="space-y-2 mt-4">
            {(data?.regional || []).length === 0 ? (
              <div className="text-xs text-ink/55 py-6 text-center">Belum ada data bagi hasil.</div>
            ) : data.regional.map((r) => (
              <div key={r.wilayah} className="flex items-center justify-between rounded-lg bg-cream p-3">
                <span className="text-sm text-ink/75">{WILAYAH_LABEL[r.wilayah] || r.wilayah}</span>
                <span className="font-display font-bold text-sm text-ink">{formatRupiah(r.total)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <Card className="rounded-xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink">Pertumbuhan Anggota (kumulatif)</h3>
        <div className="h-56 mt-4">
          <ResponsiveContainer>
            <BarChart data={growth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
              <Tooltip />
              <Bar dataKey="anggota" radius={[8, 8, 0, 0]} fill="#85A37E" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <Card className="rounded-xl border-stone-soft p-5 bg-white">
    <div className="w-9 h-9 rounded-lg bg-cream grid place-items-center text-forest"><Icon size={17} /></div>
    <div className="text-[10px] uppercase tracking-[0.16em] text-ink/55 font-semibold mt-3">{label}</div>
    <div className="font-display font-bold text-2xl text-ink mt-1 truncate">{value}</div>
  </Card>
);
